import { attendee, BoardElement } from '@/store/interface';
import { TOOLS } from './constants';
import { ImageElement, LineElement, Point, ShapeElement, TextElement } from '@/utils/interface';
import { logError, uploadFile } from '@/apis';
import { toast } from 'react-toastify';
import { UID } from 'agora-rtc-react';
import { ShortcutAction } from './interface';
import { AxiosError } from 'axios';
import utf8 from 'utf8';
import base64 from 'base-64';

export const gibberish = '4m3e2r1i@t!';

export const encryptionSwap: Record<string, string> = {
  '+': '@',
  '/': '$',
  '=': '!',
  '&': '^',
  '?': '#'
};

export const decryptionSwap: Record<string, string> = {
  '@': '+',
  $: '/',
  '!': '=',
  '^': '&',
  '#': '?'
};

export const decodeEncryptedURL = (data: string) => {
  try {
    Object.keys(decryptionSwap).map((d) => {
      const re = new RegExp(`\\${d}`, 'g');
      data = data.replace(re, decryptionSwap[d]);
    });

    data = base64.decode(data);
    data = utf8.encode(data);

    return data;
  } catch {
    return '';
  }
};

const resetScroll = () => window.scrollTo(0, 0);

export const getAlphbaticalIndex = (index: number) => String.fromCharCode(65 + index);

export const getKey = (e: KeyboardEvent) => {
  return e.key.length === 1 || e.key.toLowerCase() === 'dead'
    ? e.code.toLowerCase().split('').at(-1)
    : e.code;
};

export const getContext = (canvas: HTMLCanvasElement | null) => {
  if (canvas) return canvas.getContext('2d', { willReadFrequently: true });
  return undefined;
};

export const isDrawingTool = (tool: string) =>
  [TOOLS.PEN, TOOLS.HIGHLIGHTER, TOOLS.ERASER].includes(tool);

export const isTextData = (element: BoardElement): element is TextElement => {
  return element.type === TOOLS.TEXT;
};

export const isDrawingData = (element: BoardElement): element is LineElement => {
  return [TOOLS.PEN, TOOLS.HIGHLIGHTER, TOOLS.ERASER].includes(element.type);
};

export const isShapeData = (element: BoardElement): element is ShapeElement => {
  return [...Object.values(TOOLS.SHAPES.THREE_D), ...Object.values(TOOLS.SHAPES.TWO_D)].includes(
    element.type
  );
};

export const isImageData = (element: BoardElement): element is ImageElement => {
  return element.type === 'image';
};

export const isTwoDShape = (tool: string) => Object.values(TOOLS.SHAPES.TWO_D).includes(tool);
export const isThreeDShape = (tool: string) => Object.values(TOOLS.SHAPES.THREE_D).includes(tool);

export const getShapeTool = (tool: string) => {
  return isTwoDShape(tool) ? '2D' : isThreeDShape(tool) ? '3D' : null;
};

export const isPointInBounds = (
  point: Point,
  bounds: { x: number; y: number; width: number; height: number }
) => {
  return (
    point.x >= bounds.x &&
    point.x <= bounds.x + bounds.width &&
    point.y >= bounds.y &&
    point.y <= bounds.y + bounds.height
  );
};

export const sourceToImage = (data: ImageElement): Promise<ImageElement | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.addEventListener('load', () => {
      const newImage: ImageElement = {
        ...data,
        element: img
      };

      if (!data.width || !data.height) {
        const scrollContainer = document.getElementById('board');
        newImage.width = 0.5;
        newImage.height = (img.height * 0.5) / img.width;
        newImage.position = {
          x: 0,
          y: scrollContainer ? scrollContainer.scrollTop / scrollContainer.clientWidth : 0
        };
      }
      resolve(newImage);
    });

    img.addEventListener('error', () => {
      resolve(null);
    });
    img.src = data.src;
  });

export const htmlToImage = async (
  data: TextElement,
  makeFresh?: boolean,
  recorderEdit?: boolean
) => {
  const container = document.createElement('div');
  container.className = 'ql-container ql-snow';
  document.body.appendChild(container);

  const div = document.createElement('div');
  div.className = 'ql-editor';
  div.innerHTML = data.htmlContent;

  div.style.padding = '0';
  div.style.display = 'inline-block';
  div.style.maxWidth = `${data.maxWidth}px`;
  container.appendChild(div);

  try {
    const rect = div.getBoundingClientRect();

    const canvasWidth =
      document.getElementById('crystal-container')?.clientWidth || window.innerWidth;

    const html2canvas = (await import('html2canvas-pro')).default;

    document.addEventListener('scroll', resetScroll);
    const element = await html2canvas(div, {
      backgroundColor: null,
      logging: true,
      useCORS: true,
      allowTaint: true,
      scale: data.scale,
      width: Math.ceil(rect.width),
      height: Math.ceil(rect.height),
      scrollX: 0,
      scrollY: 0,
      foreignObjectRendering: false,
      ignoreElements(htmlElement) {
        return htmlElement.tagName === 'VIDEO';
      }
    });
    document.removeEventListener('scroll', resetScroll);

    const textData: TextElement = {
      ...data,
      element,
      ...(makeFresh
        ? {
            width: element.width / canvasWidth,
            height: element.height / canvasWidth
          }
        : recorderEdit && {
            width: element.width / (data.element.width / data.width),
            height: element.height / (data.element.width / data.width)
          })
    };
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }

    return textData;
  } catch (error) {
    console.error('Error while creating element', error);
    return null;
  }
};

export const pdfToImages = async (pdf: File | string, isWhiteboard = false) => {
  const { getDocument, GlobalWorkerOptions, version } = await import('pdfjs-dist');
  GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;

  let doc;
  const time = Date.now();
  const canvasWidth =
    document.getElementById('crystal-container')?.clientWidth || window.innerWidth;
  const pages: { file: File; name: string }[] = [];
  if (typeof pdf === 'string') {
    doc = await getDocument(pdf).promise;
  } else {
    const buffer = await pdf.arrayBuffer();
    doc = await getDocument(buffer).promise;
  }

  for (let index = 1; index <= doc.numPages; index++) {
    const fileName = `${time}-${index}.png`;
    let name;

    if (!isWhiteboard) {
      name = `Page ${index}`;
    } else if (index === 1) {
      name = 'Intro';
    } else {
      const pair = Math.floor((index - 1) / 2);
      if (index % 2 === 0) {
        name = `Q-${pair + 1}`;
      } else {
        name = `A-${pair}`;
      }
    }

    const page = await doc.getPage(index);
    const canvas = document.createElement('canvas');
    const context = getContext(canvas);
    const viewport = page.getViewport({
      scale: canvasWidth / page.getViewport({ scale: 1 }).width
    });
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    if (context) {
      await page.render({
        canvas,
        canvasContext: context,
        viewport
      }).promise;
      const blob = (await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/png')
      )) as Blob | null;
      if (blob) {
        const file = new File([blob], fileName);
        pages.push({ file, name });
      } else {
        throw new Error("Can't process PDF");
      }
    }
  }
  return pages;
};

export const errorToString = (error: object): string =>
  Object.entries(error)
    .map((entry) => {
      const [key, value] = entry;
      if (!!value && typeof value === 'object') return `${key}_${errorToString(value)}`;
      return entry.join(':_');
    })
    .join(', ');

export const logErrorApi = async (
  roomId: number,
  userIdType: number,
  userId: UID,
  exceptionType: string | number,
  description: string
) => {
  try {
    const response = await logError(roomId, userIdType, userId, exceptionType, description);
    if (response.status === 'success') {
      return response.data;
    } else {
      throw new Error(response.message);
    }
  } catch (error) {
    console.error(error);
    toast('There was an error');
    return null;
  }
};

export const getUserType = (attendee: attendee) => {
  if (attendee.isGuest) return 2;
  if (attendee.isPresenter) return 0;
  return 1;
};

export const handleFile = async (
  file: File,
  roomId: number,
  attendee: attendee | null,
  uploadType = 'ClassAssets',
  isAdmin?: boolean
) => {
  if (!attendee && !isAdmin) return;
  const userType = attendee ? getUserType(attendee) : 1;
  try {
    file = new File([file], `${Date.now()}-${file.name.replace(/\s+/g, '')}`, { type: file.type });
    const response = await uploadFile(file, roomId.toString() || '', uploadType);
    if (response.status === 'success') {
      return response.data;
    } else {
      throw new Error(response.message);
    }
  } catch (error) {
    console.error(error);
    await logErrorApi(
      roomId,
      userType,
      attendee?.id || 1,
      'Error in uploading file',
      (error as Error).toString()
    );
    if (error instanceof AxiosError) {
      toast(error.response?.data?.message || 'There was an error');
    } else toast('There was an error');
    return;
  }
};

export const nameShortener = (name: string) => {
  const shortendName = name
    .toUpperCase()
    ?.split(' ')
    .map((word, index, arr) => (index === 0 || index === arr.length - 1 ? word[0] : ''))
    .join('');
  return shortendName;
};

export const getCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  }
  return undefined;
};

export const setCookie = (name: string, value: string, days: number): void => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;

  document.cookie = `${name}=${value}; ${expires}; path=/`;
};

export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
};

export const enterFullscreen = (element: HTMLElement) => {
  const requestMethod =
    element.requestFullscreen ||
    element.webkitRequestFullscreen ||
    element.mozRequestFullScreen ||
    element.msRequestFullscreen;

  if (requestMethod) {
    return requestMethod.call(element);
  } else {
    return Promise.reject(new Error('Fullscreen API is not supported'));
  }
};

export const exitFullscreen = () => {
  const exitMethod =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;

  if (exitMethod) {
    return exitMethod.call(document);
  } else {
    return Promise.reject(new Error('Fullscreen exit API is not supported'));
  }
};

export function getShortcutKey(action: ShortcutAction): string {
  const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.userAgent);

  const shortcuts: Record<'windows' | 'mac', Record<ShortcutAction, string>> = {
    windows: {
      undo: 'Ctrl + Z',
      redo: 'Ctrl + Y',
      mic: 'Alt + A',
      video: 'Alt + V',
      screenshare: 'Alt + S',
      fullscreen: 'Alt + F',
      raise_hand: 'Alt + Y',
      global_mic: 'Alt + M',
      chat: 'Alt + H',
      participants: 'Alt + U',
      prev_page: 'Left Arrow',
      next_page: 'Right Arrow',
      next_view: 'PageUp',
      prev_view: 'PageDown'
    },
    mac: {
      undo: 'command + Z',
      redo: 'command + Shift + Z',
      mic: 'option + A',
      video: 'option + V',
      screenshare: 'option + S',
      fullscreen: 'option + F',
      raise_hand: 'option + Y',
      global_mic: 'option + M',
      chat: 'option + H',
      participants: 'option + U',
      prev_page: 'Left Arrow',
      next_page: 'Right Arrow',
      next_view: 'PageUp',
      prev_view: 'PageDown'
    }
  };

  return `(${shortcuts[isMac ? 'mac' : 'windows'][action] || 'Unknown action'})`;
}

export const countPages = (file: File): number => {
  const reader = new FileReader();
  let count = 0;

  reader.readAsText(file);

  reader.onloadend = () => {
    if (typeof reader.result === 'string') {
      const matches = reader.result.match(/\/Type[\s]*\/Page[^s]/g);
      count = matches ? matches.length : 0;
    } else {
      console.error('Failed to read file as string.');
    }
  };

  return count;
};

export function getOSInfo() {
  const ua = navigator.userAgent;

  if (/Windows NT/.test(ua)) {
    const version = ua.match(/Windows NT ([\d.]+)/)?.[1];
    if (version && parseFloat(version) >= 10.0)
      return { message: 'Operating system is compatible', valid: true };
    else return { message: 'Update to Windows 11 for optimal experience', valid: false, fix: '' };
  }

  if (/Mac OS X/.test(ua)) {
    const version = ua.match(/Mac OS X (\d+\d+(\d+)?)/)?.[1].replace(/_/g, '.');
    if (version) return { message: `Operating system is compatible`, valid: true, fix: '' };
    else
      return {
        message: `Your operating system doesn’t meet the minimum requirement for live video conferencing. Please upgrade to the minimum requirements.`,
        valid: false,
        fix: ''
      };
  }

  if (/Android/.test(ua)) {
    const version = ua.match(/Android ([\d.]+)/)?.[1];
    if (version && parseFloat(version) >= 8.0)
      return { message: `Operating system is compatible`, valid: true, fix: '' };
    else return { message: `Update to Android v8.0 for optimal experience`, valid: false, fix: '' };
  }

  if (/Linux/.test(ua)) {
    return { message: 'Operating system is compatible', valid: true, fix: '' };
  }

  return {
    message:
      'Your operating system doesn’t meet the minimum requirement for live video conferencing. Please upgrade to the minimum requirements.',
    valid: false,
    fix: ''
  };
}

export const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let name = 'Unknown';
  let version = '';
  let valid = false;
  let message = '';
  let fix = '';

  const parseVersion = (ver: string) => parseInt(ver.split('.')[0], 10);

  if (/Edg\//.test(userAgent)) {
    name = 'Edge';
    const match = userAgent.match(/Edg\/([\d.]+)/)?.[1];
    version = match || '';
    valid = parseVersion(version) >= 95;
    fix = valid ? '' : `try upgrading ${name} ${version} to ${name} 95`;
    message = valid ? 'Browser is compatible' : `${name} ${version} is not compatible`;
  } else if (/Chrome\//.test(userAgent) && !/Edg\//.test(userAgent) && !/OPR\//.test(userAgent)) {
    name = 'Chrome';
    const match = userAgent.match(/Chrome\/([\d.]+)/)?.[1];
    version = match || '';
    valid = parseVersion(version) >= 95;
    fix = valid ? '' : `try upgrading ${name} ${version} to ${name} 95`;
    message = valid ? 'Browser is compatible' : `${name} ${version} is not compatible`;
  } else if (/Firefox\//.test(userAgent)) {
    name = 'Firefox';
    const match = userAgent.match(/Firefox\/([\d.]+)/)?.[1];
    version = match || '';
    valid = true;
    fix = valid ? '' : `try upgrading you browser`;
    message = valid ? 'Browser is compatible' : `${name} ${version} is not compatible`;
  } else if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) {
    name = 'Safari';
    const match = userAgent.match(/Version\/([\d.]+)/)?.[1];
    version = match || '';
    valid = parseVersion(version) >= 14;
    fix = valid ? '' : `try upgrading ${name} ${version} to ${name} 95`;
    message = valid ? 'Browser is compatible' : `${name} ${version} is not compatible`;
  }

  return { message, fix, valid };
};

export const getNetworkInfo = (speed: number) => {
  if (speed) {
    if (speed >= 19) {
      return {
        valid: true,
        message: 'Sufficient for live class streaming.',
        fix: ''
      };
    } else {
      return {
        valid: false,
        message: 'Not sufficient for live class streaming.',
        fix: 'Upgrade your internet plan.'
      };
    }
  } else {
    return {
      valid: false,
      message: 'Not sufficient for live class streaming.',
      fix: 'Upgrade your internet plan.'
    };
  }
};

export const detectUrls = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  // const urlRegex = /[a-zA-Z]+?\.[a-zA-Z]+/;
  return text.match(urlRegex) || [];
};

export const getFileType = (url: string): 'link' | 'image' | 'pdf' => {
  const extension = url.split('.').pop()?.toLowerCase().split('?')[0];
  if (!extension) return 'link';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
    return 'image';
  }
  if (extension === 'pdf') {
    return 'pdf';
  }
  return 'link';
};
export const isPPTFile = (file: File) => {
  const mime = file.type;
  const name = file.name || '';

  const mimeRegex =
    /^application\/vnd\.(ms-powerpoint(\..+)?|openxmlformats-officedocument\.presentationml\.(presentation|slideshow|template)|oasis\.opendocument\.presentation)$/;
  const extRegex = /\.(pptx?|ppsx?|potx?|pptm|ppsm|potm|ppam?|ppa|odp)$/i;

  return mimeRegex.test(mime) || extRegex.test(name);
};
